import { FunctionComponent, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useTranslation } from "react-i18next";

import { Modal } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize, FontSize } from "@/components/Button/types";
import { ListSourceType } from "@/enum/list.enum";
import { classNames } from "@/utils/classNames";

const ListIcon = () => (
	<svg
		className="mr-1.5"
		fill="none"
		height="14"
		stroke="currentColor"
		strokeLinecap="round"
		strokeLinejoin="round"
		strokeWidth="2"
		viewBox="0 0 24 24"
		width="14"
	>
		<line x1="8" x2="21" y1="6" y2="6" />
		<line x1="8" x2="21" y1="12" y2="12" />
		<line x1="8" x2="21" y1="18" y2="18" />
		<line x1="3" x2="3.01" y1="6" y2="6" />
		<line x1="3" x2="3.01" y1="12" y2="12" />
		<line x1="3" x2="3.01" y1="18" y2="18" />
	</svg>
);

interface StepperProps {
	listId: number;
	doneSteps: string[];
	availableSteps: string[];
	currentStep: string;
	isDirty?: boolean;
	sourceType?: ListSourceType;
}

const StepIcon = ({
	index,
	isActive,
	isDone,
}: {
	index: number;
	isActive: boolean;
	isDone: boolean;
}) => {
	if (isDone) {
		return (
			<div className="w-5 h-5 rounded-full bg-status-success flex items-center justify-center flex-shrink-0">
				<svg fill="none" height="10" stroke="white" strokeWidth="2" viewBox="0 0 10 10" width="10">
					<path d="M2 5l2 2 4-4" />
				</svg>
			</div>
		);
	}

	if (isActive) {
		return (
			<div className="w-5 h-5 rounded-full bg-brand-light flex items-center justify-center flex-shrink-0">
				<span className="text-[11px] font-semibold text-white leading-4">{index}</span>
			</div>
		);
	}

	return (
		<div className="w-5 h-5 rounded-full border-[1.5px] border-gray-200 flex items-center justify-center flex-shrink-0">
			<span className="text-[11px] font-semibold text-text-disabled leading-4">{index}</span>
		</div>
	);
};

export const Stepper: FunctionComponent<StepperProps> = ({
	listId,
	doneSteps,
	availableSteps,
	currentStep,
	isDirty,
	sourceType,
}) => {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const ts = (key: string) => t(`list.stepper.${key}`);

	// Smart lists filter prospects/contacts (not companies), so the shared "filter" label
	// ("Company Qualification") is misleading there — use a dedicated label.
	const stepLabel = (step: string) =>
		sourceType === ListSourceType.SMART_LIST && step === "filter"
			? ts("smartListFilter")
			: ts(step);

	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const [pendingStep, setPendingStep] = useState<string>("");

	const handleTabClick = (step: string) => {
		if (isDirty) {
			setPendingStep(step);
			setIsModalOpen(true);
		} else {
			if (step === "leadList") {
				navigate(`/app/lists/detail/${listId}`);
			} else {
				navigate(`/app/lists/create/${step}/${listId}`);
			}
		}
	};

	const handleConfirmNavigation = () => {
		setIsModalOpen(false);
		if (pendingStep === "leadList") {
			navigate(`/app/lists/detail/${listId}`);
		} else {
			navigate(`/app/lists/create/${pendingStep}/${listId}`);
		}
	};

	return (
		// Full-bleed bar: cancel the ListBuilderLayout body's uniform p-4 so the step bar sits
		// flush under the header and its bottom border spans the full width (like the app's tab bars).
		// Sticky at top-12 pins it directly beneath the layout's 48px (h-12) header while content
		// scrolls; bg-white keeps scrolling content from showing through the pinned bar.
		// z-[101]: the bar shares the ListBuilderLayout body's (isolated) stacking context with the
		// step content, whose Tooltip triggers are z-100 (see components/Tooltip). Without a higher
		// z-index those rows paint over the pinned bar while scrolling behind it (PREC-3748). The
		// body's `isolate` caps this at the header, so z-[101] never lifts the bar above the header.
		<div className="sticky top-12 z-[101] -mx-4 -mt-4 mb-6 bg-white">
			<div className="flex flex-row items-center justify-between border-b border-border-light">
				<div className="flex flex-row items-center">
					{availableSteps.map((step, index) => {
						const isSelected = currentStep === step;
						const isDone = doneSteps.includes(step);

						return (
							<button
								key={`tab-${step}`}
								className={classNames(
									"flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors relative whitespace-nowrap cursor-pointer border-b-2",
									isSelected
										? "text-brand-light border-b-brand-light"
										: isDone
											? "text-text-meta border-b-transparent hover:text-text"
											: "text-text-disabled border-b-transparent hover:text-text-meta"
								)}
								type="button"
								onClick={() => handleTabClick(step)}
							>
								<StepIcon index={index + 1} isActive={isSelected} isDone={isDone} />
								{stepLabel(step)}
							</button>
						);
					})}
				</div>
				<div className="flex items-center gap-3 pr-4">
					<div className="w-px h-5 bg-border-light" />
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						fontSize={FontSize.XS}
						image={<ListIcon />}
						size={ButtonSize.XS}
						title={ts("viewLeadList")}
						type="button"
						onClick={() => handleTabClick("leadList")}
					/>
				</div>
			</div>
			{isDirty && (
				<Modal
					handleClose={() => setIsModalOpen(false)}
					handleSave={handleConfirmNavigation}
					isOpened={isModalOpen}
					submitButtonText="Leave"
					title="Unsaved Changes"
				>
					<div>Do you want to leave this page?</div>
				</Modal>
			)}
		</div>
	);
};
